/**
 * Report body log grouping — consecutive activity-set s/n (#1, #2, #3) form one
 * set with a single total/count footer. Gaps in s/n start a new set.
 * Used in previews and PDF (not the description editor).
 */

import {
  COMBINED_SKU_DETAILS_PREVIEW_MAX,
  formatCombinedSkuDetailsPreview,
} from 'pages/Logs/activityLogsDisplay';
import {
  formatReportLogAmountDisplay,
  formatReportLogDateTime,
  formatReportLogDateTimeRange,
  parseReportLogAmount,
} from './reportActivityLogs';
import {
  ReportDescriptionBlock,
  ReportLogBlock,
  ReportTextBlock,
  splitLogsByConsecutiveActivitySerial,
} from './reportDescriptionBlocks';

/** Row shape for reporting type rollup (uses /logs Combine SKUs setting). */
export type ReportCombinableLogRow = {
  sourceId: string,
  tmpId: number,
  type: string,
  message: string,
  date?: string,
  value?: string,
  units?: string,
  valueMode?: 'value' | 'units',
  /** Actual log type / SKU — unique names on a combined row. */
  activityLogType?: string,
};

export type CombinedReportLogRow = ReportCombinableLogRow & {
  /** Frozen source ids merged into this row — for highlight / insert toggles. */
  combinedSourceIds: string[],
  /** How many logs were rolled up (1 when unique type). */
  logCount: number,
  /** Datetimes of every merged log — used for the Combine SKUs span label. */
  sourceDates: string[],
  /** First 20 unique source details — combined row only. */
  sourceMessages: string[],
  /** How many unique details exist (may exceed the 20 stored). */
  uniqueSourceCount: number,
  /** Preview text: combined notice, type, then unique activityLogs. */
  combinedDetails?: string,
};

const reportRowAmount = (
  row: { value?: string, units?: string },
  valueMode: 'value' | 'units',
): number | null => (
  parseReportLogAmount(valueMode === 'units' ? row.units : row.value)
);

const toStandaloneReportLogRow = (row: ReportCombinableLogRow): CombinedReportLogRow => ({
  ...row,
  combinedSourceIds: [row.sourceId],
  logCount: 1,
  sourceDates: row.date ? [row.date] : [],
  sourceMessages: [],
  uniqueSourceCount: 0,
});

const finishCombinedReportLogRow = (combined: CombinedReportLogRow): CombinedReportLogRow => ({
  ...combined,
  message: combined.logCount > 1 ? combined.type : combined.message,
  date: combined.logCount > 1
    ? formatReportLogDateTimeRange(combined.sourceDates)
    : combined.date,
  combinedDetails: combined.logCount > 1
    ? formatCombinedSkuDetailsPreview(
      combined.sourceMessages,
      combined.logCount,
      combined.uniqueSourceCount,
    )
    : undefined,
});

/**
 * Merge cash/units rows by type — same as /logs Combine SKUs.
 * Empty / N/A (non-balance) rows stay as their own activity lines.
 */
export const combineReportLogsByType = (
  rows: ReportCombinableLogRow[],
  valueMode: 'value' | 'units' = 'value',
): CombinedReportLogRow[] => {
  const amountKey = valueMode === 'units' ? 'units' : 'value';
  const byType = new Map<string, CombinedReportLogRow>();
  const seenDetailsByType = new Map<string, Set<string>>();
  const slots: Array<{ kind: 'combined', type: string } | { kind: 'row', row: CombinedReportLogRow }> = [];

  const rememberUniqueDetail = (type: string, activityName: string | undefined) => {
    const text = String(activityName || '').trim();
    if (!text) return;
    let seen = seenDetailsByType.get(type);
    if (!seen) {
      seen = new Set<string>();
      seenDetailsByType.set(type, seen);
    }
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const bucket = byType.get(type);
    if (!bucket) return;
    bucket.uniqueSourceCount += 1;
    if (bucket.sourceMessages.length < COMBINED_SKU_DETAILS_PREVIEW_MAX) {
      bucket.sourceMessages.push(text);
    }
  };

  rows.forEach((row) => {
    // N/A / empty amounts are not rolled up — they stay visible as activity logs.
    if (reportRowAmount(row, valueMode) == null) {
      slots.push({ kind: 'row', row: toStandaloneReportLogRow(row) });
      return;
    }
    const type = String(row.type || 'Activity').trim() || 'Activity';
    const existing = byType.get(type);
    if (!existing) {
      byType.set(type, {
        ...row,
        type,
        combinedSourceIds: [row.sourceId],
        logCount: 1,
        sourceDates: row.date ? [row.date] : [],
        sourceMessages: [],
        uniqueSourceCount: 0,
      });
      rememberUniqueDetail(type, row.activityLogType || row.type);
      slots.push({ kind: 'combined', type });
      return;
    }
    existing.combinedSourceIds.push(row.sourceId);
    existing.logCount += 1;
    if (row.date) existing.sourceDates.push(row.date);
    rememberUniqueDetail(type, row.activityLogType || row.type);
    const prevAmt = parseReportLogAmount(existing[amountKey]);
    const nextAmt = parseReportLogAmount(row[amountKey]);
    if (prevAmt != null || nextAmt != null) {
      existing[amountKey] = String((prevAmt || 0) + (nextAmt || 0));
    }
  });

  return slots.map((slot) => {
    if (slot.kind === 'row') return finishCombinedReportLogRow(slot.row);
    return finishCombinedReportLogRow(byType.get(slot.type)!);
  });
};

/** Chip label — type for combined cash/units rows; N/A logs keep the activity message. */
export const reportLogDisplayMessage = (
  log: {
    type?: string,
    message?: string,
    value?: string,
    units?: string,
    valueMode?: 'value' | 'units',
  },
  combineLogTypes: boolean,
  valueMode: 'value' | 'units' = 'value',
): string => {
  const type = String(log.type || '').trim();
  const message = String(log.message || '').trim();
  const mode = log.valueMode === 'units' || log.valueMode === 'value'
    ? log.valueMode
    : valueMode;
  const hasAmount = reportRowAmount(log, mode) != null;
  if (combineLogTypes && type && hasAmount) return type;
  return message || type || '—';
};

/** Preview/PDF details — combined notice for rolled-up cash; N/A rows stay as activity text. */
export const reportLogPreviewDetails = (
  log: {
    type?: string,
    message?: string,
    combinedDetails?: string,
    value?: string,
    units?: string,
    valueMode?: 'value' | 'units',
  },
  combineLogTypes: boolean,
  valueMode: 'value' | 'units' = 'value',
): string => {
  const details = String(log.combinedDetails || '').trim();
  if (combineLogTypes && details) return details;
  return reportLogDisplayMessage(log, combineLogTypes, valueMode);
};

/** Readable datetime (or Combine SKUs span such as "1 day" / "1 year 2 months"). */
export const reportLogDateTimeLabel = (log: { date?: string }): string => (
  formatReportLogDateTime(log.date)
);

/** Within one consecutive log group, merge same-type rows for preview/PDF display. */
export const combineLogGroupByTypeForDisplay = (
  logs: ReportLogBlock[],
  valueMode: 'value' | 'units',
): ReportLogBlock[] => (
  combineReportLogsByType(logs, valueMode).map((row) => ({
    id: `combined_${row.combinedSourceIds.join('_')}`,
    kind: 'log' as const,
    sourceId: row.combinedSourceIds[0],
    tmpId: row.tmpId,
    // Combined cash/units rows use the type label; N/A rows keep the activity message.
    message: row.logCount > 1 ? row.type : row.message,
    type: row.type,
    date: row.date,
    value: row.value,
    units: row.units,
    valueMode,
    // Keep merged ids so a combined row can open that exact log set in ActivityLogs.
    combinedSourceIds: row.combinedSourceIds,
    combinedDetails: row.combinedDetails,
    activityLogType: row.activityLogType,
  }))
);

export type ReportPreviewTextSegment = {
  kind: 'text',
  id: string,
  text: string,
};

export type ReportPreviewLogGroupSegment = {
  kind: 'logGroup',
  id: string,
  logs: ReportLogBlock[],
};

export type ReportPreviewPictureSegment = {
  kind: 'picture',
  id: string,
  imageId: string,
  fileName: string,
  localDataUri?: string,
};

export type ReportPreviewSegment =
  | ReportPreviewTextSegment
  | ReportPreviewLogGroupSegment
  | ReportPreviewPictureSegment;

/**
 * Consecutive activity s/n → one group with a total; a gap (1,2,3 then 8)
 * starts a new group. Typed body text stays between those groups.
 */
export const groupDescriptionBlocksForReportPreview = (
  blocks: ReportDescriptionBlock[],
): ReportPreviewSegment[] => {
  const segments: ReportPreviewSegment[] = [];
  let pendingLogs: ReportLogBlock[] = [];

  const flushLogs = () => {
    if (!pendingLogs.length) return;
    splitLogsByConsecutiveActivitySerial(pendingLogs).forEach((run) => {
      segments.push({
        kind: 'logGroup',
        id: `loggroup_${run[0].id}`,
        logs: run,
      });
    });
    pendingLogs = [];
  };

  blocks.forEach((block) => {
    if (block.kind === 'log') {
      pendingLogs.push(block);
      return;
    }
    if (block.kind === 'picture') {
      flushLogs();
      segments.push({
        kind: 'picture',
        id: block.id,
        imageId: block.imageId,
        fileName: block.fileName,
        ...(block.localDataUri ? { localDataUri: block.localDataUri } : {}),
      });
      return;
    }
    const text = String((block as ReportTextBlock).text || '').trim();
    // Empty cursor lines do not split groups — s/n gaps still split in flush.
    if (!text) return;
    flushLogs();
    segments.push({
      kind: 'text',
      id: block.id,
      text,
    });
  });
  flushLogs();
  return segments;
};

export type ReportLogGroupTotalSummary = {
  /** "Total" when amounts exist; "Log count" when they do not. */
  term: 'Total' | 'Log count',
  /** Formatted amount or count for display. */
  figure: string,
  /** How many logs contributed a parseable amount (for Total). */
  amountLogCount: number,
  logCount: number,
};

/**
 * One footer figure for a grouped log set.
 * Amounts → Total · £1,234 (or units). No amounts → Log count · 3.
 */
export const summarizeReportLogGroupTotal = (
  logs: { value?: string, units?: string }[],
  valueMode: 'value' | 'units',
  currency: string,
): ReportLogGroupTotalSummary => {
  const logCount = logs.length;
  let sum = 0;
  let amountLogCount = 0;
  logs.forEach((log) => {
    const raw = valueMode === 'units' ? log.units : log.value;
    const amount = parseReportLogAmount(raw);
    if (amount == null) return;
    sum += amount;
    amountLogCount += 1;
  });

  if (amountLogCount === 0) {
    return {
      term: 'Log count',
      figure: String(logCount),
      amountLogCount: 0,
      logCount,
    };
  }

  const display = formatReportLogAmountDisplay(
    valueMode,
    currency,
    valueMode === 'value' ? String(sum) : undefined,
    valueMode === 'units' ? String(sum) : undefined,
  );

  return {
    term: 'Total',
    figure: display.label,
    amountLogCount,
    logCount,
  };
};

/** Builder highlight blue — same as report composer log chips. */
export const REPORT_LOG_CHIP_BLUE = '#2563eb';
export const REPORT_LOG_CHIP_BLUE_SOFT = 'rgba(37, 99, 235, 0.08)';
export const REPORT_LOG_CHIP_BLUE_BORDER = 'rgba(37, 99, 235, 0.4)';
export const REPORT_LOG_CHIP_BLUE_MID = 'rgba(37, 99, 235, 0.12)';
